import React, { useCallback, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import DialogContaCorrenteDetalhe from "pages/comercial/components/DialogContaCorrenteDetalhe";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

const formatValor = (v) =>
  Number(v || 0).toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function FabricantesContaCorrente() {
  const { showAlert } = useAlert();
  const { temFuncao, userData } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const [loading, setLoading] = useState(false);

  const [fabricantes, setFabricantes] = useState([]);
  const [fabricanteInput, setFabricanteInput] = useState("");
  const [fabricanteSelecionado, setFabricanteSelecionado] = useState(null);

  const [form, setForm] = useState({
    datainicial: "",
    datafinal: "",
  });

  const [movimentos, setMovimentos] = useState([]);

  const [campanhas, setCampanhas] = useState([]);
  const [incentivos, setIncentivos] = useState([]);
  const [descontosCliente, setDescontosCliente] = useState([]);

  const [openDetalhe, setOpenDetalhe] = useState(false);
  const [detalheOrigem, setDetalheOrigem] = useState("");
  const [detalheMes, setDetalheMes] = useState("");
  const [detalheLinhas, setDetalheLinhas] = useState([]);

  const abrirDetalhe = useCallback((origem, mes, linhas) => {
    setDetalheOrigem(origem);
    setDetalheMes(mes);
    setDetalheLinhas(Array.isArray(linhas) ? linhas : []);
    setOpenDetalhe(true);
  }, []);

  const tabelaMensal = useMemo(() => {
    if (!form.datainicial || !form.datafinal) return [];

    const obterPeriodo = (valor) => {
      if (!valor) return "";

      const texto = String(valor);

      if (/^\d{4}-\d{2}$/.test(texto)) {
        return texto;
      }

      if (/^\d{4}-\d{2}-\d{2}/.test(texto)) {
        return texto.substring(0, 7);
      }

      return "";
    };

    const meses = new Map();

    const criarMes = (periodo) => {
      if (!periodo || meses.has(periodo)) return;

      const [ano, mes] = periodo.split("-").map(Number);

      meses.set(periodo, {
        periodo,
        ano,
        mes,
        mesLabel: (() => {
          const texto = new Date(ano, mes - 1, 1).toLocaleDateString("pt-PT", {
            month: "long",
            year: "numeric",
          });

          return texto.charAt(0).toUpperCase() + texto.slice(1);
        })(),

        campanhas: 0,
        campanhasLinhas: [],

        incentivos: 0,
        incentivosLinhas: [],

        descontosCliente: 0,
        descontosClienteLinhas: [],

        recebido: 0,
        recebimentosLinhas: [],

        ajustes: 0,
        ajustesLinhas: [],

        totalApurado: 0,
        saldoMes: 0,
        saldoTotal: 0,
      });
    };

    const [anoInicial, mesInicial] = form.datainicial.substring(0, 7).split("-").map(Number);
    const [anoFinal, mesFinal] = form.datafinal.substring(0, 7).split("-").map(Number);

    let cursor = new Date(anoInicial, mesInicial - 1, 1);
    const limite = new Date(anoFinal, mesFinal - 1, 1);

    while (cursor <= limite) {
      const periodo = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
      criarMes(periodo);
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    campanhas.forEach((linha) => {
      const periodo = obterPeriodo(linha?.periodo || linha?.datainicial);
      criarMes(periodo);

      const registo = meses.get(periodo);
      if (!registo) return;

      registo.campanhas += Number(linha?.valor || 0);
      registo.campanhasLinhas.push(linha);
    });

    incentivos.forEach((linha) => {
      const periodo = obterPeriodo(linha?.datafinal);
      if (!periodo) return;

      const registo = meses.get(periodo);
      if (!registo) return;

      registo.incentivos += Number(linha?.valor || 0);
      registo.incentivosLinhas.push(linha);
    });

    descontosCliente.forEach((linha) => {
      const periodo = obterPeriodo(linha?.faturadata);
      criarMes(periodo);

      const registo = meses.get(periodo);
      if (!registo) return;

      registo.descontosCliente += Number(linha?.valor_receber_fabricante || 0);
      registo.descontosClienteLinhas.push(linha);
    });

    movimentos.forEach((linha) => {
      const periodo = obterPeriodo(linha?.data_movimento);
      criarMes(periodo);

      const registo = meses.get(periodo);
      if (!registo) return;

      const tipo = String(linha?.tipo_movimento || "");
      const valor = Number(linha?.valor || 0);

      if (tipo === "RECEBIMENTO") {
        registo.recebido += Math.abs(valor);
        registo.recebimentosLinhas.push(linha);
      }

      if (tipo === "AJUSTE") {
        registo.ajustes += valor;
        registo.ajustesLinhas.push(linha);
      }
    });

    let saldoAcumulado = 0;

    return Array.from(meses.values())
      .sort((a, b) => a.periodo.localeCompare(b.periodo))
      .map((linha) => {
        const totalApurado = linha.campanhas + linha.incentivos + linha.descontosCliente;

        const saldoMes = totalApurado - linha.recebido + linha.ajustes;

        saldoAcumulado += saldoMes;

        return {
          ...linha,
          totalApurado,
          saldoMes,
          saldoTotal: saldoAcumulado,
        };
      });
  }, [form.datainicial, form.datafinal, campanhas, incentivos, descontosCliente, movimentos]);

  const totais = useMemo(() => {
    const apurado = tabelaMensal.reduce((acc, linha) => acc + Number(linha.totalApurado || 0), 0);

    const recebido = tabelaMensal.reduce((acc, linha) => acc + Number(linha.recebido || 0), 0);

    const ajustes = tabelaMensal.reduce((acc, linha) => acc + Number(linha.ajustes || 0), 0);

    const saldo = tabelaMensal.length
      ? Number(tabelaMensal[tabelaMensal.length - 1].saldoTotal || 0)
      : 0;

    return {
      apurado,
      recebido,
      ajustes,
      saldo,
    };
  }, [tabelaMensal]);

  const columns = useMemo(
    () => [
      {
        Header: "Mês",
        accessor: "mesLabel",
        width: 170,
        sortType: "textPt",
        Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{value}</span>,
      },
      {
        Header: "Campanhas",
        accessor: "campanhas",
        width: 175,
        sortType: "numPt",
        Cell: ({ value, row }) => (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            gap={0.5}
            sx={{
              minWidth: 0,
              whiteSpace: "nowrap",
            }}
          >
            <span>{formatValor(value)} €</span>

            <IconButton
              size="small"
              color="info"
              title="Ver detalhe das campanhas"
              disabled={!row.original?.campanhasLinhas?.length}
              onClick={() =>
                abrirDetalhe(
                  "CAMPANHAS",
                  row.original?.periodo || "",
                  row.original?.campanhasLinhas || []
                )
              }
              sx={{
                p: 0.25,
                ml: 0.25,
                flexShrink: 0,
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ),
      },
      {
        Header: "Incentivos",
        accessor: "incentivos",
        width: 175,
        sortType: "numPt",
        Cell: ({ value, row }) => (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            gap={0.5}
            sx={{
              minWidth: 0,
              whiteSpace: "nowrap",
            }}
          >
            <span>{formatValor(value)} €</span>

            <IconButton
              size="small"
              color="info"
              title="Ver detalhe dos incentivos"
              disabled={!row.original?.incentivosLinhas?.length}
              onClick={() =>
                abrirDetalhe(
                  "INCENTIVOS",
                  row.original?.periodo || "",
                  row.original?.incentivosLinhas || []
                )
              }
              sx={{
                p: 0.25,
                ml: 0.25,
                flexShrink: 0,
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ),
      },
      {
        Header: "Descontos Cliente",
        accessor: "descontosCliente",
        width: 200,
        sortType: "numPt",
        Cell: ({ value, row }) => (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            gap={0.5}
            sx={{
              minWidth: 0,
              whiteSpace: "nowrap",
            }}
          >
            <span>{formatValor(value)} €</span>

            <IconButton
              size="small"
              color="info"
              title="Ver detalhe dos descontos"
              disabled={!row.original?.descontosClienteLinhas?.length}
              onClick={() =>
                abrirDetalhe(
                  "DESCONTOS_CLIENTE",
                  row.original?.periodo || "",
                  row.original?.descontosClienteLinhas || []
                )
              }
              sx={{
                p: 0.25,
                ml: 0.25,
                flexShrink: 0,
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ),
      },
      {
        Header: "Total Apurado",
        accessor: "totalApurado",
        width: 155,
        sortType: "numPt",
        Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{formatValor(value)} €</span>,
      },
      {
        Header: "Recebido",
        accessor: "recebido",
        width: 175,
        sortType: "numPt",
        Cell: ({ value, row }) => (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            gap={0.5}
            sx={{
              minWidth: 0,
              whiteSpace: "nowrap",
            }}
          >
            <span>{formatValor(value)} €</span>

            <IconButton
              size="small"
              color="info"
              title="Ver detalhe dos recebimentos"
              disabled={!row.original?.recebimentosLinhas?.length}
              onClick={() =>
                abrirDetalhe(
                  "RECEBIMENTOS",
                  row.original?.periodo || "",
                  row.original?.recebimentosLinhas || []
                )
              }
              sx={{
                p: 0.25,
                ml: 0.25,
                flexShrink: 0,
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ),
      },
      {
        Header: "Saldo no Mês",
        accessor: "saldoMes",
        width: 150,
        sortType: "numPt",
        Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{formatValor(value)} €</span>,
      },
      {
        Header: "Saldo no Total",
        accessor: "saldoTotal",
        width: 155,
        sortType: "numPt",
        Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{formatValor(value)} €</span>,
      },
    ],
    [abrirDetalhe]
  );

  const carregarFabricantes = async (term = "") => {
    const [payload, err] = await handleApi(
      api.post("FabricantesContaCorrente/SelFabricantes", { term })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    setFabricantes(Array.isArray(payload) ? payload : []);
  };

  const consultar = async () => {
    if (!isAdminLike) {
      showAlert("Sem permissões para aceder a esta página.", "error");
      return;
    }

    if (!fabricanteSelecionado?.ref || !form.datainicial || !form.datafinal) {
      showAlert("Seleciona fabricante, data inicial e data final.", "warning");
      return;
    }

    if (form.datainicial > form.datafinal) {
      showAlert("A data inicial não pode ser superior à data final.", "warning");
      return;
    }

    setLoading(true);

    try {
      const payloadBase = {
        reffabricante: fabricanteSelecionado.ref,
        datainicial: form.datainicial,
        datafinal: form.datafinal,
      };

      const [
        [dadosCampanhas, errCampanhas],
        [dadosIncentivos, errIncentivos],
        [dadosDescontos, errDescontos],
        [dadosMovimentos, errMovimentos],
      ] = await Promise.all([
        handleApi(
          api.post("FabricantesContaCorrente/SyncContaCorrenteCampanhas", {
            ...payloadBase,
            idutilizador: userData?.id || null,
          })
        ),

        handleApi(
          api.post("Comercial/SyncContaCorrenteIncentivos", {
            ...payloadBase,
            idutilizador: userData?.id || null,
          })
        ),

        handleApi(
          api.post("ClientesAcordosMateriais/SelValorReceberFabricante", {
            ...payloadBase,
            idclienteacordomaterial: null,
          })
        ),

        handleApi(api.post("FabricantesContaCorrente/SelContaCorrente", payloadBase)),
      ]);

      if (errCampanhas) {
        showAlert(errCampanhas, "error");
        return;
      }

      if (errIncentivos) {
        showAlert(errIncentivos, "error");
        return;
      }

      if (errDescontos) {
        showAlert(errDescontos, "error");
        return;
      }

      if (errMovimentos) {
        showAlert(errMovimentos, "error");
        return;
      }

      const movimentosReais = (Array.isArray(dadosMovimentos) ? dadosMovimentos : []).filter(
        (linha) =>
          String(linha?.tipo_movimento || "") === "RECEBIMENTO" ||
          String(linha?.tipo_movimento || "") === "AJUSTE"
      );

      setCampanhas(Array.isArray(dadosCampanhas) ? dadosCampanhas : []);
      setIncentivos(Array.isArray(dadosIncentivos) ? dadosIncentivos : []);
      setDescontosCliente(Array.isArray(dadosDescontos) ? dadosDescontos : []);
      setMovimentos(movimentosReais);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={{ xs: 1.5, md: 3 }}>
            <MDTypography variant="h6" mb={2}>
              Reembolsos de fabricantes
            </MDTypography>

            <MDBox
              display="grid"
              gap={1.5}
              sx={{
                gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr auto" },
                alignItems: "end",
              }}
            >
              <Autocomplete
                size="small"
                options={fabricantes}
                filterOptions={(x) => x}
                inputValue={fabricanteInput}
                value={fabricanteSelecionado}
                getOptionLabel={(option) => (option?.nome ? `${option.nome} (${option.ref})` : "")}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setFabricanteInput(value);
                    carregarFabricantes(value);
                  }

                  if (reason === "clear") {
                    setFabricanteInput("");
                    setFabricanteSelecionado(null);
                  }
                }}
                onOpen={() => carregarFabricantes(fabricanteInput)}
                onChange={(_, value) => {
                  setFabricanteSelecionado(value);
                  setFabricanteInput(value?.nome ? `${value.nome} (${value.ref})` : "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Fabricante"
                    placeholder="Selecione..."
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />

              <MDInput
                label="Data inicial"
                type="date"
                value={form.datainicial}
                onChange={(e) => setForm((p) => ({ ...p, datainicial: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <MDInput
                label="Data final"
                type="date"
                value={form.datafinal}
                onChange={(e) => setForm((p) => ({ ...p, datafinal: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <MDButton color="info" onClick={consultar} disabled={!isAdminLike}>
                Consultar
              </MDButton>
            </MDBox>

            <MDBox
              mt={3}
              display="grid"
              gap={1.5}
              sx={{
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                },
              }}
            >
              <Card variant="outlined">
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Apurado
                  </MDTypography>

                  <MDTypography variant="h5">{formatValor(totais.apurado)} €</MDTypography>
                </MDBox>
              </Card>

              <Card variant="outlined">
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Recebido
                  </MDTypography>

                  <MDTypography variant="h5">{formatValor(totais.recebido)} €</MDTypography>
                </MDBox>
              </Card>

              <Card variant="outlined">
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Saldo final
                  </MDTypography>

                  <MDTypography variant="h5">{formatValor(totais.saldo)} €</MDTypography>
                </MDBox>
              </Card>
            </MDBox>

            <MDBox mt={3} maxWidth="100%" sx={{ overflow: "auto" }}>
              <ProjectTable
                tableKey="fabricantes-conta-corrente-mensal"
                version="1"
                title="Conta corrente mensal"
                subtitle={`${tabelaMensal.length} mês(es) no período consultado`}
                columns={columns}
                data={tabelaMensal}
                loading={loading}
                emptyMessage="Sem movimentos no período selecionado."
                minWidth={1180}
                maxHeight="55vh"
                defaultSortBy={[{ id: "periodo", desc: false }]}
                showKeepFilters
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      <DialogContaCorrenteDetalhe
        open={openDetalhe}
        onClose={() => setOpenDetalhe(false)}
        origem={detalheOrigem}
        mes={detalheMes}
        linhas={detalheLinhas}
      />
    </DashboardLayout>
  );
}
